import { useEffect, useState } from "react";
import { t } from "ttag";

import DateTime from "metabase/common/components/DateTime";
import { PaginationControls } from "metabase/common/components/PaginationControls";
import { usePagination } from "metabase/common/hooks/use-pagination";
import * as Urls from "metabase/lib/urls";
import {
  Anchor,
  Button,
  Flex,
  Icon,
  Loader,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "metabase/ui";
import type { CardId, DashboardId } from "metabase-types/api";

import S from "./NativeQueryHistoryButton.module.css";
import { getNativeQueryHistory } from "./api";
import type {
  NativeQueryHistoryEntry,
  NativeQueryHistoryResponse,
  NativeQueryHistoryScope,
} from "./types";

const ICON_SIZE = 18;
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE = 300;
const ROW_CELL_STYLE = { verticalAlign: "middle" } as const;

const sourceLabel = (entry: NativeQueryHistoryEntry) => {
  if (entry.source_type === "dashboard") {
    return entry.dashboard_name ?? t`Dashboard #${entry.dashboard_id}`;
  }
  if (entry.source_type === "question") {
    return entry.card_name ?? t`Question #${entry.card_id}`;
  }
  return t`Ad-hoc`;
};

const sourceUrl = (entry: NativeQueryHistoryEntry) => {
  if (entry.source_type === "dashboard" && entry.dashboard_id !== null) {
    return Urls.dashboard({
      id: entry.dashboard_id as DashboardId,
      name: entry.dashboard_name ?? undefined,
    });
  }
  if (entry.source_type === "question" && entry.card_id !== null) {
    return Urls.question({
      id: entry.card_id as CardId,
      name: entry.card_name ?? undefined,
    });
  }
  return null;
};

const Source = ({ entry }: { entry: NativeQueryHistoryEntry }) => {
  const label = sourceLabel(entry);
  const url = sourceUrl(entry);

  return url ? (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" fw="bold">
      {label}
    </Anchor>
  ) : (
    label
  );
};

const databaseLabel = (entry: NativeQueryHistoryEntry) =>
  entry.database_name ?? t`Database #${entry.database_id}`;

const durationLabel = (runningTime: number) => {
  if (runningTime < 1000) {
    return t`${runningTime} ms`;
  }
  return t`${(runningTime / 1000).toFixed(1)} s`;
};

const newestHistoryEntriesFirst = (entries: NativeQueryHistoryEntry[]) =>
  [...entries].sort((left, right) => {
    const startedAtDifference =
      Date.parse(right.started_at) - Date.parse(left.started_at);

    return startedAtDifference || right.id - left.id;
  });

const HistoryTable = ({
  rows,
  onApplySQL,
}: {
  rows: NativeQueryHistoryEntry[];
  onApplySQL?: (sql: string) => void;
}) => {
  if (rows.length === 0) {
    return (
      <Flex className={S.centeredState} align="center" justify="center">
        <Text c="text-light" fw="bold">{t`No matching SQL history.`}</Text>
      </Flex>
    );
  }

  return (
    <div className={S.tableContainer}>
      <table className={S.table}>
        <thead>
          <tr>
            <th className={S.sqlColumn}>{t`SQL`}</th>
            <th className={S.sourceColumn}>{t`Source`}</th>
            <th className={S.databaseColumn}>{t`Database`}</th>
            <th className={S.timeColumn}>{t`Executed at`}</th>
            <th className={S.statusColumn}>{t`Status`}</th>
            <th className={S.actionColumn}>{t`Action`}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => (
            <tr key={entry.id}>
              <td style={ROW_CELL_STYLE}>
                <pre className={S.sql}>{entry.sql}</pre>
              </td>
              <td style={ROW_CELL_STYLE}>
                <Source entry={entry} />
              </td>
              <td style={ROW_CELL_STYLE}>{databaseLabel(entry)}</td>
              <td style={ROW_CELL_STYLE}>
                <Stack gap="xs">
                  <DateTime value={entry.started_at} unit="minute" />
                  <Text c="text-light" size="xs">
                    {durationLabel(entry.running_time)}
                  </Text>
                </Stack>
              </td>
              <td className={S.statusCell} style={ROW_CELL_STYLE}>
                <Text
                  className={S.statusText}
                  c={entry.status === "failed" ? "error" : "success"}
                  fw="bold"
                  h="1.875rem"
                  lh={1}
                  size="xs"
                >
                  {entry.status === "failed" ? t`Failed` : t`Completed`}
                </Text>
              </td>
              <td className={S.actionCell} style={ROW_CELL_STYLE}>
                <Button
                  aria-label={t`Apply SQL`}
                  className={S.applyButton}
                  disabled={!onApplySQL}
                  h="1.875rem"
                  px="xs"
                  size="xs"
                  variant="outline"
                  onClick={() => onApplySQL?.(entry.sql)}
                >
                  {t`Apply`}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const NativeQueryHistoryModal = ({
  onClose,
  onSQLChange,
}: {
  onClose: () => void;
  onSQLChange?: (queryText: string) => void;
}) => {
  const { page, setPage, handleNextPage, handlePreviousPage } = usePagination();
  const [scope, setScope] = useState<NativeQueryHistoryScope>("ad-hoc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [result, setResult] = useState<NativeQueryHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    getNativeQueryHistory(
      {
        scope,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: PAGE_SIZE * page,
      },
      controller.signal,
    )
      .then(setResult)
      .catch((requestError: unknown) => {
        if (
          !controller.signal.aborted &&
          (!(requestError instanceof DOMException) ||
            requestError.name !== "AbortError")
        ) {
          setError(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [page, requestVersion, scope, search]);

  const rows = newestHistoryEntriesFirst(result?.data ?? []);
  const handleApplySQL = onSQLChange
    ? (sql: string) => {
        onSQLChange(sql);
        onClose();
      }
    : undefined;

  return (
    <Modal opened onClose={onClose} size="90%" title={t`Native query history`}>
      <Stack className={S.modalBody} gap="md">
        <Flex align="end" gap="md" justify="space-between" wrap="wrap">
          <SegmentedControl<NativeQueryHistoryScope>
            value={scope}
            data={[
              { value: "ad-hoc", label: t`Ad-hoc` },
              { value: "all", label: t`All queries` },
            ]}
            onChange={(value) => {
              setPage(0);
              setScope(value);
            }}
          />
          <TextInput
            aria-label={t`Search SQL`}
            leftSection={<Icon name="search" />}
            placeholder={t`Search SQL`}
            value={searchInput}
            w="24rem"
            onChange={(event) => {
              setPage(0);
              setSearchInput(event.currentTarget.value);
            }}
          />
        </Flex>

        <div className={S.results}>
          {error ? (
            <Flex className={S.centeredState} align="center" justify="center">
              <Stack align="center" gap="sm">
                <Text c="error">{t`Unable to load Native query history.`}</Text>
                <Button onClick={() => setRequestVersion((value) => value + 1)}>
                  {t`Retry`}
                </Button>
              </Stack>
            </Flex>
          ) : (
            <HistoryTable rows={rows} onApplySQL={handleApplySQL} />
          )}
          {loading && (
            <Flex
              className={S.loadingOverlay}
              data-testid="native-query-history-loading"
              align="center"
              justify="center"
            >
              <Loader size="sm" />
            </Flex>
          )}
        </div>

        <div className={S.pagination}>
          {result && !error && (
            <PaginationControls
              page={page}
              pageSize={PAGE_SIZE}
              itemsLength={rows.length}
              onNextPage={!loading && result.has_more ? handleNextPage : null}
              onPreviousPage={!loading ? handlePreviousPage : null}
            />
          )}
        </div>
      </Stack>
    </Modal>
  );
};

export const NativeQueryHistoryButton = ({
  onSQLChange,
}: {
  onSQLChange?: (queryText: string) => void;
}) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Tooltip label={t`Native query history`}>
        <Button
          variant="subtle"
          className={S.button}
          aria-label={t`Native query history`}
          p={0}
          leftSection={<Icon name="history" size={ICON_SIZE} />}
          onClick={() => setOpened(true)}
        />
      </Tooltip>
      {opened && (
        <NativeQueryHistoryModal
          onClose={() => setOpened(false)}
          onSQLChange={onSQLChange}
        />
      )}
    </>
  );
};
